import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, TrendingUp, Users, BarChart3, Smartphone, Zap, Shield } from 'lucide-react';

const ZohoCRM = () => {
  const features = [
    {
      icon: <Users className="w-6 h-6 text-blue-700" />,
      title: 'Contactbeheer',
      description: 'Centraliseer al uw klantinformatie en bouw sterke relaties op.',
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-blue-700" />,
      title: 'Salesrapportages',
      description: 'Real-time inzicht in uw verkoopprestaties en pipeline.',
    },
    {
      icon: <Zap className="w-6 h-6 text-blue-700" />,
      title: 'Automatisering',
      description: 'Automatiseer repetitieve taken en focus op verkopen.',
    },
    {
      icon: <Smartphone className="w-6 h-6 text-blue-700" />,
      title: 'Mobiel CRM',
      description: 'Toegang tot uw CRM data altijd en overal.',
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-blue-700" />,
      title: 'Leadmanagement',
      description: 'Verzamel, kwalificeer en converteer leads efficiënt.',
    },
    {
      icon: <Shield className="w-6 h-6 text-blue-700" />,
      title: 'Veilig & Betrouwbaar',
      description: 'Enterprise-grade beveiliging voor uw klantdata.',
    },
  ];

  const benefits = [
    'Verhoog uw salesproductiviteit met 30-40%',
    'Verkort de salescyclus door betere leads tracking',
    'Verbeter klantrelaties met 360-graden klantinzicht',
    'Automatiseer routinetaken en bespaar uren per week',
    'Maak datagedreven beslissingen met realtime analytics',
    'Schaal mee met uw bedrijfsgroei',
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                #1 CRM Software Wereldwijd
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Zoho CRM: De Slimme Keuze voor Groeiende Bedrijven
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Transformeer uw salesproces met het meest gebruiksvriendelijke CRM systeem. 
                Wij helpen u met implementatie, configuratie en optimalisatie van Zoho CRM.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    Vraag een Demo Aan
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                
                <a href="tel:0638823471">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                  >
                    Bel: 06 3882 3471
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxkYXNoYm9hcmR8ZW58MHx8fHwxNzczODE3NjA4fDA&ixlib=rb-4.1.0&q=85"
                alt="Zoho CRM Dashboard"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Zoho CRM Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho CRM?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho CRM is een krachtige Customer Relationship Management oplossing die bedrijven helpt om hun 
                verkoop-, marketing- en supportactiviteiten te stroomlijnen. Met meer dan 250.000 bedrijven wereldwijd 
                is Zoho CRM de gekozen oplossing voor organisaties die willen groeien.
              </p>
              <p>
                Als gecertificeerd Zoho Partner heeft Guijs Consultancy uitgebreide ervaring met het implementeren 
                van Zoho CRM voor Nederlandse bedrijven. Wij begrijpen de lokale markt en zorgen ervoor dat het 
                systeem perfect aansluit op uw specifieke bedrijfsprocessen.
              </p>
              <p>
                Of u nu een klein bedrijf bent dat net begint met CRM, of een middelgrote onderneming die haar 
                salesprocessen wil professionaliseren - Zoho CRM biedt de flexibiliteit en schaalbaarheid die u nodig heeft.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Krachtige Functies van Zoho CRM
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alles wat u nodig heeft om uw salesproces te optimaliseren en omzet te verhogen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxkYXNoYm9hcmR8ZW58MHx8fHwxNzczODE3NjA4fDA&ixlib=rb-4.1.0&q=85"
                alt="CRM Analytics"
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Voordelen van Zoho CRM voor Uw Bedrijf
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Zoho CRM is meer dan alleen een database met contacten. Het is een complete oplossing die 
                uw hele salesorganisatie transformeert en meetbare resultaten oplevert.
              </p>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Guijs Consultancy voor Uw Zoho CRM Implementatie?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij zijn niet zomaar een implementatiepartner - wij zijn uw strategische adviseur die 
              zorgt voor een succesvolle CRM transformatie.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Gecertificeerde Expertise</h3>
                  <p className="text-gray-600">
                    Ons team bestaat uit gecertificeerde Zoho consultants met jarenlange ervaring 
                    in complexe CRM projecten.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Lokale Kennis</h3>
                  <p className="text-gray-600">
                    Wij begrijpen de Nederlandse markt en zorgen voor implementaties die aansluiten 
                    bij lokale wet- en regelgeving.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Snelle Implementatie</h3>
                  <p className="text-gray-600">
                    Door onze ervaren aanpak bent u binnen enkele weken operationeel met uw nieuwe CRM systeem.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Continue Support</h3>
                  <p className="text-gray-600">
                    Ook na go-live staan wij voor u klaar met support, training en optimalisaties.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ons Implementatieproces
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Van intake tot go-live - wij begeleiden u in elke stap.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '1',
                  title: 'Intakegesprek',
                  description: 'Wij analyseren uw huidige situatie, wensen en doelstellingen.',
                },
                {
                  step: '2',
                  title: 'Configuratie & Maatwerk',
                  description: 'Wij richten Zoho CRM in volgens uw specifieke bedrijfsprocessen.',
                },
                {
                  step: '3',
                  title: 'Datamilgratie',
                  description: 'Veilige migratie van uw bestaande klantdata naar Zoho CRM.',
                },
                {
                  step: '4',
                  title: 'Training & Go-Live',
                  description: 'Wij trainen uw team en begeleiden de lancering.',
                },
                {
                  step: '5',
                  title: 'Optimalisatie & Support',
                  description: 'Continue ondersteuning en optimalisatie van uw CRM omgeving.',
                },
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center text-xl font-bold">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar om te starten met Zoho CRM?"
        subtitle="Plan een gratis demo en ontdek hoe Zoho CRM uw salesproces kan transformeren."
      />
    </div>
  );
};

export default ZohoCRM;
